import { type IssuerInfo, consentPracticalPoints } from '@domain/legal/IssuerInfo';
/**
 * Textes en français simple de l'interface de synchronisation Hybride.
 * Aucune donnée métier ici : uniquement des libellés fixes.
 */
import type { SyncAttentionItem } from '@infra/sync/hybrid/SyncBridgeTypes';
import { rejectionMessageFr } from '@infra/sync/hybrid/HybridSyncEngine';

export const RESOURCE_FR: Readonly<Record<string, string>> = {
  'tenant-profile': 'Profil de l’entreprise',
  salaries: 'Salarié',
  bulletins: 'Bulletin de paie',
  ecritures: 'Écriture comptable',
  factures: 'Facture',
  'centres-analytiques': 'Centre analytique',
};

export const ACTION_FR: Readonly<Record<string, string>> = { upsert: 'Modification', delete: 'Suppression' };

const QUARANTINE_REASON_FR: Readonly<Record<string, string>> = {
  initial_merge:
    'Votre copie locale différait de celle du serveur : celle du serveur a été conservée. Votre copie est mise de côté, rien n’est perdu.',
  payslip_locked: 'Le bulletin a été validé ou payé entre-temps : votre modification n’a pas été appliquée.',
};

export function itemTitle(item: SyncAttentionItem): string {
  const what = RESOURCE_FR[item.resource] ?? 'Fiche';
  return `${ACTION_FR[item.action] ?? 'Modification'} — ${what}`;
}

/** Motif lisible : jamais de code technique. */
export function itemReason(item: SyncAttentionItem): string {
  if (item.kind === 'quarantine') return QUARANTINE_REASON_FR[item.code ?? ''] ?? 'Copie locale mise de côté.';
  if (item.message && item.message.trim()) return item.message;
  return item.code ? rejectionMessageFr(item.code) : 'Modification refusée par le serveur.';
}

/** Mention montrée sous le motif : ce qui est arrivé à la modification, et ce que l'utilisateur peut faire. */
export function itemHelp(item: SyncAttentionItem): string {
  if (item.kind === 'quarantine') return 'Vous pouvez en faire une nouvelle fiche, ou l’abandonner.';
  if (item.code === 'payslip_locked') return 'Le bulletin validé reste inchangé. Vous pouvez recopier votre version dans un bulletin de correction.';
  return 'Votre modification est conservée sur ce poste tant que vous ne l’abandonnez pas.';
}

export const TEXT = {
  panelTitle: 'Synchronisation',
  consentTitle: 'Activer la synchronisation (offre Hybride)',
  loadingState: 'Chargement de l’état de la synchronisation…',
  loadingList: 'Chargement de la liste…',
  listError: 'Impossible de lire la liste pour le moment. Vos modifications sont conservées sur ce poste.',
  listEmpty: 'Rien à examiner : aucune modification n’a été refusée.',
  attentionHeading: 'À examiner',
  attentionIntro:
    'Ces modifications n’ont pas pu être appliquées automatiquement. Aucune n’est perdue : choisissez ce qu’il faut en faire.',
  noStateYet: 'L’état de la synchronisation n’est pas encore connu. Il s’affichera dès que le poste aura contacté le serveur.',
  reconnect: 'Se reconnecter',
  reconnectConfirmTitle: 'Se reconnecter',
  reconnectConfirmMessage:
    'Vous allez être déconnecté puis revenir à l’écran de connexion. Terminez d’abord la saisie en cours : elle sera conservée, mais non enregistrée si elle est incomplète.',
  update: 'Mettre à jour l’application',
  syncNow: 'Synchroniser maintenant',
  syncing: 'Synchronisation en cours…',
  diagnostic: 'Exporter le diagnostic',
  withdraw: 'Retirer mon consentement',
  withdrawTitle: 'Retirer le consentement',
  withdrawMessage:
    'La synchronisation sera arrêtée. Vos données restent sur ce poste, mais ne seront plus envoyées au serveur ni visibles sur le site Nexxus.',
  close: 'Fermer',
  abandon: 'Abandonner',
  retry: 'Réessayer',
  copy: 'Copier en nouvelle fiche',
  firstSendTitle: 'Premier envoi',
  firstSendRunning: 'Premier envoi en cours. Vous pouvez continuer à travailler.',
  firstSendSummaryTitle: 'Résumé du premier envoi',
  accept: 'Activer la synchronisation',
  notAdmin: 'La synchronisation doit être activée par un administrateur de l’entreprise.',
  consentFailed: 'Impossible d’activer la synchronisation pour le moment. Vos données ne sont pas modifiées.',
  consentOk: 'Synchronisation activée.',
} as const;

/** Paragraphes ajoutés à l'écran de consentement : premier envoi quand poste et serveur contiennent déjà des données. */
export const CONSENT_FIRST_SEND_TITLE = 'Si votre poste et le serveur contiennent déjà des données';
export const CONSENT_FIRST_SEND_PARAGRAPHS: readonly string[] = [
  'Au premier envoi, Nexxus compare les fiches de votre poste avec celles du serveur. Les fiches identiques ne bougent pas. Les fiches qui n’existent que sur votre poste sont envoyées. Celles qui n’existent que sur le serveur sont reçues.',
  'Quand une même fiche existe des deux côtés avec un contenu différent, la version du serveur est conservée. Votre copie locale n’est pas supprimée : elle est mise de côté dans la liste « À examiner », où vous pouvez la copier en nouvelle fiche ou l’abandonner.',
  'Un résumé du premier envoi s’affiche à la fin. Vous pouvez continuer à travailler pendant ce temps.',
];

/**
 * Passages « pratiques » du consentement (durée, hébergement, contact) : construits à partir des informations de
 * l'émetteur saisies dans la console (voir domain/legal/IssuerInfo). Aucun passage entre crochets : une valeur
 * inconnue donne une formulation neutre, et la vérification de publication refuse tout crochet restant.
 */
export function buildConsentPracticalPoints(info: IssuerInfo): ReadonlyArray<{ title: string; text: string }> {
  return consentPracticalPoints(info);
}
